import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
    principal_name: "",
    cell_phone: "",
    email: "",
    dealership_name: "",
    location: "",
    retail_units_per_month: "",
    ros_per_month: "",
};

const FIELDS = [
    {
        name: "principal_name",
        label: "Dealer Principal / GM Name",
        type: "text",
        placeholder: "Jane Smith",
        testid: "dealer-form-name-input",
        span: false,
    },
    {
        name: "cell_phone",
        label: "Cell Phone",
        type: "tel",
        placeholder: "(555) 123-4567",
        testid: "dealer-form-phone-input",
        span: false,
    },
    {
        name: "email",
        label: "Email",
        type: "email",
        placeholder: "jane@dealership.com",
        testid: "dealer-form-email-input",
        span: false,
    },
    {
        name: "location",
        label: "Location (City, State)",
        type: "text",
        placeholder: "Phoenix, AZ",
        testid: "dealer-form-location-input",
        span: false,
    },
    {
        name: "dealership_name",
        label: "Dealership Name",
        type: "text",
        placeholder: "Smith Auto Group",
        testid: "dealer-form-dealership-input",
        span: true,
    },
    {
        name: "retail_units_per_month",
        label: "Total Retail Units / Month",
        type: "number",
        placeholder: "150",
        testid: "dealer-form-units-input",
        span: false,
    },
    {
        name: "ros_per_month",
        label: "Total ROs / Month",
        type: "number",
        placeholder: "900",
        testid: "dealer-form-ros-input",
        span: false,
    },
];

export const DealerInquiryForm = ({ open, onOpenChange }) => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await fetch(`${API}/dealer-inquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("submit failed");
            toast.success("Inquiry received", {
                description:
                    "Our dealer partnerships team will reach out shortly — a confirmation email is on its way to your inbox.",
            });
            trackEvent("lead_submit");
            setForm(EMPTY);
            onOpenChange(false);
        } catch {
            toast.error("Something went wrong", {
                description: "Please try again in a moment.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="dealer-form-modal"
                className="max-h-[92vh] overflow-y-auto border-white/10 bg-[#121722] text-white sm:max-w-xl"
            >
                <DialogTitle className="font-display text-xl font-bold tracking-tight">
                    Become a <span className="text-sky-400">Partner Dealer</span>
                </DialogTitle>
                <p className="text-sm text-slate-400">
                    Tell us about your store and our dealer partnerships team
                    will reach out.
                </p>
                <form
                    onSubmit={submit}
                    data-testid="dealer-inquiry-form"
                    className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2"
                >
                    {FIELDS.map((f) => (
                        <div
                            key={f.name}
                            className={f.span ? "sm:col-span-2" : ""}
                        >
                            <Label
                                htmlFor={f.name}
                                className="text-xs font-semibold uppercase tracking-wider text-slate-400"
                            >
                                {f.label}
                            </Label>
                            <Input
                                id={f.name}
                                data-testid={f.testid}
                                type={f.type}
                                placeholder={f.placeholder}
                                required
                                min={f.type === "number" ? 0 : undefined}
                                value={form[f.name]}
                                onChange={set(f.name)}
                                className="mt-1.5 h-11 border-white/15 bg-[#0C1019] text-white placeholder:text-slate-600 focus-visible:ring-sky-400"
                            />
                        </div>
                    ))}
                    <button
                        data-testid="dealer-form-submit-btn"
                        type="submit"
                        disabled={sending}
                        className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-sky-400 py-3.5 text-sm font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_36px_rgba(56,189,248,0.45)] active:scale-95 disabled:opacity-60 sm:col-span-2"
                    >
                        {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                        {sending ? "Sending…" : "Submit Partnership Inquiry"}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
};
